import React, { useState } from "react";

import cardAmex from "../../assets/img/payment-methods/card-american-express.svg";
import cardDclub from "../../assets/img/payment-methods/card-dinersclub.svg";
import cardDiscover from "../../assets/img/payment-methods/card-discover.svg";
import cardJcb from "../../assets/img/payment-methods/card-jcb.svg";
import cardMaestro from "../../assets/img/payment-methods/card-maestro.svg";
import cardMaster from "../../assets/img/payment-methods/card-master.svg";
import cardPaypal from "../../assets/img/payment-methods/card-paypal.svg";
import cardUnion from "../../assets/img/payment-methods/card-unionpay.svg";
import cardVisa from "../../assets/img/payment-methods/card-visa.svg";
import { Skeleton } from "../ui/skeleton";

import "./subscriptionCardFolder.css";

const CONTOUR_RADII = Array.from({ length: 17 }, (_, index) => 72 + index * 24);

const PAYMENT_METHOD_IMAGES = {
  american_express: cardAmex,
  amex: cardAmex,
  diners_club: cardDclub,
  discover: cardDiscover,
  jcb: cardJcb,
  maestro: cardMaestro,
  master: cardMaster,
  mastercard: cardMaster,
  paypal: cardPaypal,
  union_pay: cardUnion,
  unionpay: cardUnion,
  visa: cardVisa,
};

const getPaymentMethodImage = (value) => {
  const normalizedValue = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  return PAYMENT_METHOD_IMAGES[normalizedValue] || null;
};

const SubscriptionCardFolder = ({
  subscription,
  onAction,
  actionLabel = "Open subscription actions",
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const paymentMethod = subscription?.paymentMethod || {};
  const cardholderName = String(
    paymentMethod.cardholderName || "Cardholder name unavailable"
  ).toUpperCase();
  const cardType = paymentMethod.cardType || paymentMethod.label || "Card";
  const paymentMethodImage = getPaymentMethodImage(cardType);
  const lastFour = String(paymentMethod.lastFour || "")
    .replace(/\D/g, "")
    .slice(-4);
  const expiry = paymentMethod.expiry || "--/--";
  const updatePaymentMethodUrl =
    subscription?.updatePaymentMethodUrl || subscription?.update_url || "";
  const canUpdatePaymentMethod = subscription?.canUpdatePaymentMethod !== false;
  const maskedCardNumber = lastFour ? `•••• •••• •••• ${lastFour}` : "Card details unavailable";

  if (!subscription) {
    return (
      <section
        aria-busy="true"
        aria-label="Loading payment method"
        className={`subscription-card-folder-panel subscription-card-folder-panel--loading ${className}`.trim()}
      >
        <div className="subscription-card-folder-panel__heading">
          <Skeleton className="subscription-card-folder-panel__skeleton-title" />
        </div>
        <Skeleton className="subscription-card-folder-panel__skeleton-card" />
        <div className="subscription-card-folder-panel__footer">
          <Skeleton className="subscription-card-folder-panel__skeleton-hint" />
        </div>
      </section>
    );
  }

  const toggleFolder = () => setIsOpen((current) => !current);

  return (
    <section className={`subscription-card-folder-panel ${className}`.trim()}>
      <div className="subscription-card-folder-panel__heading">
        <div>
          <h2 className="subscription-card-folder-panel__title">Payment method</h2>
        </div>
      </div>

      <div
        className={`subscription-card-folder ${isOpen ? "subscription-card-folder--open" : ""}`}
        data-open={isOpen ? "true" : "false"}
      >
        <div className="subscription-card-folder__hit-area">
          <span className="subscription-card-folder__pocket-back" aria-hidden="true" />
          <button
            type="button"
            className="subscription-card-folder__toggle"
            aria-label={`${isOpen ? "Close" : "Open"} ${subscription.planName} plan card`}
            aria-expanded={isOpen}
            onClick={toggleFolder}
          >
            <span className="subscription-card-folder__card">
              <span className="subscription-card-folder__contours" aria-hidden="true">
                <svg viewBox="0 0 640 404" preserveAspectRatio="none">
                  {CONTOUR_RADII.map((radius, index) => (
                    <circle
                      key={radius}
                      cx="-20"
                      cy="-28"
                      r={radius}
                      fill="none"
                      stroke="currentColor"
                      strokeOpacity={0.32 - index * 0.01}
                      strokeWidth="1.5"
                    />
                  ))}
                </svg>
              </span>
              <span className="subscription-card-folder__card-glow" aria-hidden="true" />
              <span className="subscription-card-folder__card-topline">
                <span className="subscription-card-folder__cardholder">
                  {cardholderName}
                </span>
                <span className="subscription-card-folder__card-type-fallback">
                  {cardType}
                </span>
              </span>
              <span className="subscription-card-folder__card-plan">
                <span className="subscription-card-folder__card-number">
                  {maskedCardNumber}
                </span>
                <span
                  className="subscription-card-folder__chip"
                  aria-label="Contactless payment chip"
                  role="img"
                >
                  <span />
                  <span />
                  <span />
                  <span />
                </span>
              </span>
              <span className="subscription-card-folder__card-bottomline">
                <span className="subscription-card-folder__card-expiry">
                  <span>EXP</span>
                  <strong>{expiry}</strong>
                </span>
              </span>
            </span>
          </button>

        </div>

        <span className="subscription-card-folder__pocket-front" aria-hidden={isOpen}>
          <svg viewBox="0 0 384 110" preserveAspectRatio="none" aria-hidden="true">
            <path
              d="M0 17C15 7 31 4 49 4H87C110 4 126 17 144 32L158 44C176 59 206 59 225 43L240 30C257 16 271 4 295 4H335C354 4 370 8 384 18V94C384 103 377 110 368 110H16C7 110 0 103 0 94Z"
            />
            <path
              d="M10 21C22 13 35 11 51 11H85C105 11 120 23 137 37L153 50C175 68 208 68 231 49L246 36C262 23 275 11 297 11H333C350 11 363 14 374 22V89C374 97 369 101 360 101H24C15 101 10 96 10 89Z"
            />
          </svg>
          <span className="subscription-card-folder__pocket-content">
            <span className="subscription-card-folder__renewal">
              {paymentMethodImage ? (
                <img loading="lazy"
                  className="subscription-card-folder__pocket-logo"
                  src={paymentMethodImage}
                  alt=""
                />
              ) : (
                <strong>{cardType}</strong>
              )}
              <span className="subscription-card-folder__pocket-expiry">
                <span>EXP</span>
                <strong>{expiry}</strong>
              </span>
            </span>
            <span className="subscription-card-folder__details-row">
              <span className="subscription-card-folder__name">
                {cardholderName}
              </span>
              <span className="subscription-card-folder__reference">
                <strong>{maskedCardNumber}</strong>
              </span>
            </span>
          </span>
        </span>
      </div>

      <div className="subscription-card-folder-panel__footer">
        <p className="subscription-card-folder-panel__hint">
          Tap the card to {isOpen ? "close" : "open"} details.
        </p>
        <div className="subscription-card-folder-panel__actions">
          {updatePaymentMethodUrl && canUpdatePaymentMethod ? (
            <a
              className="subscription-card-folder__update-link"
              href={updatePaymentMethodUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Change payment method
            </a>
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default SubscriptionCardFolder;
